import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import MarketingShell from "@/components/MarketingShell";
import NoIndex from "@/components/NoIndex";
import { DriftingLetters, FloatingNotes } from "@/components/BackgroundFx";

const VIDEO_URL =
  "https://d1yei2z3i6k35z.cloudfront.net/12793360/6a3ee16da37167.66744368_ComposeYourselfWaitlistTYVideo.mp4";
const WHATSAPP_URL = "https://chat.whatsapp.com/BXx47XXin66GbaAfo21jIz?mode=gi_t";
const FROM_EMAIL = "hello@danberrymusic.com";

const STEPS = [
  {
    n: "01",
    eyebrow: "Press play",
    title: "Watch this quick message from Dan.",
    body: "Three minutes on what Compose Yourself! actually is, why he's bringing it to Chabad houses first, and what happens next.",
  },
  {
    n: "02",
    eyebrow: "Inbox check",
    title: (
      <>
        Whitelist <em style={{ color: "#F2D589", fontStyle: "italic" }}>{FROM_EMAIL}</em>{" "}
        so you don&apos;t miss launch day.
      </>
    ),
    body: (
      <>
        Add <strong style={{ color: "#F5EFE3" }}>{FROM_EMAIL}</strong> to your
        contacts. If you&apos;re on Gmail, drag the first email into the{" "}
        <strong style={{ color: "#F5EFE3" }}>Primary</strong> tab so booking
        details don&apos;t land in Promotions. Two seconds now, no scrambling later.
      </>
    ),
  },
  {
    n: "03",
    eyebrow: "Backstage Pass",
    title: (
      <>
        Join the{" "}
        <em style={{ color: "#F2D589", fontStyle: "italic" }}>
          Compose Yourself! Backstage Pass
        </em>{" "}
        WhatsApp group.
      </>
    ),
    body: "Behind-the-scenes voice notes, sneak previews of new pieces, first dibs on dates, and a direct line to Dan. The room before the room.",
    cta: {
      href: WHATSAPP_URL,
      label: "Join on WhatsApp",
      testId: "thankyou-whatsapp-cta",
      variant: "whatsapp",
    },
  },
  {
    n: "04",
    eyebrow: "Get to know Dan",
    title: (
      <>
        Read Dan&apos;s story: the path from{" "}
        <em style={{ color: "#F2D589", fontStyle: "italic" }}>improv</em> to{" "}
        <em style={{ color: "#F2D589", fontStyle: "italic" }}>Compose Yourself!</em>
      </>
    ),
    body: "Where the idea came from, what he's been chasing, and the conversations that became the show.",
    cta: {
      href: "/about",
      label: "Meet Dan \u2192",
      testId: "thankyou-about-cta",
      internal: true,
    },
  },
  {
    n: "05",
    eyebrow: "Pay it forward",
    title: (
      <>
        Know another shliach who would{" "}
        <em style={{ color: "#F2D589", fontStyle: "italic" }}>love</em> this for their community?
      </>
    ),
    body: "The first cohort of Founding Communities is small on purpose. If someone you know should be in the room, send them the page.",
    cta: {
      href: "/waitlist",
      label: "Share the waitlist \u2192",
      testId: "thankyou-share-cta",
      internal: true,
    },
  },
];

function StepCTA({ cta }) {
  if (!cta) return null;
  const isWhatsApp = cta.variant === "whatsapp";
  const className =
    "inline-flex items-center justify-center gap-2 rounded-full px-6 h-11 text-xs uppercase tracking-[0.22em] mt-4 whitespace-nowrap transition-transform hover:scale-[1.02]";
  const style = isWhatsApp
    ? {
        backgroundColor: "#25D366",
        color: "#0a3d24",
        fontWeight: 700,
        boxShadow: "0 0 28px rgba(37,211,102,0.45), 0 8px 24px rgba(37,211,102,0.25)",
      }
    : {
        backgroundColor: "transparent",
        color: "#F2D589",
        border: "1px solid #F2D589",
        fontWeight: 600,
        boxShadow: "0 0 28px rgba(232,196,138,0.25)",
      };
  const inner = (
    <>
      {isWhatsApp && (
        <svg
          aria-hidden="true"
          viewBox="0 0 32 32"
          width="18"
          height="18"
          fill="currentColor"
          className="shrink-0"
        >
          <path d="M16.003 3.2c-7.07 0-12.8 5.73-12.8 12.8 0 2.26.6 4.46 1.74 6.4L3.2 28.8l6.6-1.73a12.78 12.78 0 0 0 6.2 1.6h.01c7.06 0 12.8-5.74 12.8-12.8 0-3.42-1.33-6.63-3.75-9.05A12.7 12.7 0 0 0 16 3.2zm.01 23.34h-.01a10.62 10.62 0 0 1-5.42-1.48l-.39-.23-4.04 1.06 1.08-3.94-.25-.4a10.6 10.6 0 0 1-1.63-5.65c0-5.86 4.78-10.63 10.66-10.63 2.84 0 5.51 1.11 7.52 3.12a10.55 10.55 0 0 1 3.12 7.52c0 5.86-4.78 10.63-10.64 10.63zm5.82-7.97c-.32-.16-1.88-.93-2.17-1.03-.29-.11-.5-.16-.71.16-.21.32-.82 1.03-1 1.24-.18.21-.37.24-.69.08-.32-.16-1.34-.49-2.55-1.57-.94-.84-1.58-1.88-1.77-2.2-.18-.32-.02-.49.14-.65.14-.14.32-.37.48-.55.16-.18.21-.32.32-.53.11-.21.05-.4-.03-.55-.08-.16-.71-1.72-.98-2.36-.26-.62-.52-.53-.71-.54l-.61-.01c-.21 0-.55.08-.84.4-.29.32-1.1 1.08-1.1 2.64s1.13 3.06 1.29 3.27c.16.21 2.22 3.39 5.39 4.76.75.32 1.34.52 1.8.66.76.24 1.45.21 1.99.13.61-.09 1.88-.77 2.14-1.51.26-.74.26-1.38.18-1.51-.08-.13-.29-.21-.61-.37z" />
        </svg>
      )}
      <span>{cta.label}</span>
    </>
  );
  if (cta.internal) {
    return (
      <Link to={cta.href} data-testid={cta.testId} className={className} style={style}>
        {inner}
      </Link>
    );
  }
  return (
    <a
      href={cta.href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={cta.testId}
      className={className}
      style={style}
    >
      {inner}
    </a>
  );
}

export default function ThankYou() {
  return (
    <MarketingShell active="/waitlist">
      <NoIndex />
      <DriftingLetters count={32} />
      <FloatingNotes count={6} />

      {/* Confirmation banner */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="rounded-full px-5 py-2 text-center mb-4 mx-auto w-fit"
        style={{
          backgroundColor: "transparent",
          color: "#F2D589",
          border: "1px solid #F2D589",
          fontWeight: 700,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          fontSize: "11px",
          boxShadow: "0 0 40px rgba(232,196,138,0.4)",
        }}
        data-testid="thankyou-banner"
      >
        {"\u2713 You're on the Priority Waitlist"}
      </motion.div>

      {/* Hero — full-width single-line headline so video + step 1 land above the fold */}
      <section className="text-center">
        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.05 }}
          className="leading-[0.92]"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 500,
            fontSize: "clamp(40px, 6.8vw, 96px)",
            textWrap: "balance",
            letterSpacing: "-0.005em",
            marginBottom: "0.4em",
          }}
          data-testid="thankyou-title"
        >
          You&rsquo;re in.{" "}
          <em style={{ color: "#F2D589" }}>Now let&rsquo;s set you up properly.</em>
        </motion.h1>
      </section>

      {/* Video */}
      <motion.section
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.25 }}
        className="mt-5 rounded-2xl overflow-hidden border max-w-[700px] mx-auto"
        style={{
          borderColor: "rgba(232,196,138,0.35)",
          backgroundColor: "rgba(8,4,25,0.55)",
          boxShadow: "0 30px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(232,196,138,0.18) inset",
        }}
        data-testid="thankyou-video-wrap"
      >
        <video
          src={VIDEO_URL}
          controls
          playsInline
          preload="metadata"
          className="block w-full h-auto"
          style={{ aspectRatio: "16 / 9", backgroundColor: "#000" }}
          data-testid="thankyou-video"
        >
          Your browser doesn&rsquo;t support embedded video. You can{" "}
          <a href={VIDEO_URL} target="_blank" rel="noopener noreferrer">
            watch it here
          </a>
          .
        </video>
      </motion.section>

      {/* Scroll cue — nudges users on shorter laptops to scroll past the video */}
      <motion.div
        className="flex flex-col items-center mt-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.9 }}
        data-testid="thankyou-scroll-cue"
        aria-hidden="true"
      >
        <span
          className="text-[10px] uppercase tracking-[0.32em] mb-1"
          style={{ color: "rgba(245,239,227,0.55)" }}
        >
          Five quick steps below
        </span>
        <motion.svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#F2D589"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
        >
          <polyline points="6 9 12 15 18 9" />
        </motion.svg>
      </motion.div>

      {/* Step list */}
      <section className="mt-6 max-w-[760px] mx-auto">
        <ol className="space-y-5" data-testid="thankyou-steps">
          {STEPS.map((step, idx) => (
            <motion.li
              key={step.n}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: idx * 0.04 }}
              className="rounded-2xl border p-6 sm:p-7 backdrop-blur-md flex gap-5"
              style={{
                borderColor: "rgba(255,255,255,0.12)",
                backgroundColor: "rgba(8,4,25,0.45)",
              }}
              data-testid={`thankyou-step-${step.n}`}
            >
              <div className="shrink-0">
                <div
                  className="rounded-full w-12 h-12 flex items-center justify-center text-base font-semibold"
                  style={{
                    backgroundColor: "rgba(232,196,138,0.14)",
                    color: "#F2D589",
                    border: "1px solid rgba(232,196,138,0.35)",
                    fontFamily: "'Courier Prime', monospace",
                    letterSpacing: "0.05em",
                  }}
                >
                  {step.n}
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <p
                  className="text-[10px] uppercase tracking-[0.32em] mb-2"
                  style={{ color: "rgba(245,239,227,0.55)" }}
                >
                  {step.eyebrow}
                </p>
                <h3
                  className="text-xl sm:text-2xl leading-snug"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                >
                  {step.title}
                </h3>
                <p
                  className="mt-3 text-sm sm:text-base leading-relaxed"
                  style={{ color: "rgba(245,239,227,0.78)" }}
                >
                  {step.body}
                </p>
                <StepCTA cta={step.cta} />
              </div>
            </motion.li>
          ))}
        </ol>
      </section>

      {/* Soft footer note */}
      <section
        className="mt-16 mb-4 rounded-2xl border p-6 sm:p-8 text-center max-w-[680px] mx-auto"
        style={{ borderColor: "rgba(255,255,255,0.1)", backgroundColor: "rgba(8,4,25,0.35)" }}
      >
        <p
          className="text-base sm:text-2xl leading-relaxed"
          style={{
            color: "rgba(245,239,227,0.82)",
            fontFamily: "'Cormorant Garamond', serif",
            fontStyle: "italic",
          }}
        >
          Thank you for being one of the early ones. The first few rooms set
          the tone for everything that comes after.
        </p>
        <p
          className="mt-3 text-xs"
          style={{
            color: "rgba(245,239,227,0.55)",
            letterSpacing: "0.22em",
            textTransform: "uppercase",
          }}
        >
          {"\u2014 Dan"}
        </p>
      </section>
    </MarketingShell>
  );
}
